// Lignes de la liste des enfants attendus : collage depuis un tableur et validation.
// Colonnes imposées : Nom, Prénom, Date de naissance, Email d'un parent (facultatif).
const stripAccents = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
const FIELDS = ['lastName', 'firstName', 'birthDate', 'parentEmail'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (text) => EMAIL_RE.test((text || '').trim());

/**
 * Cellules d'un collage depuis un tableur (texte à tabulations). Renvoie null pour une
 * valeur unique (collage ordinaire dans un champ). Lignes vides et en-tête Nom / Prénom retirés.
 */
export function parseClipboard(text) {
    if (!/[\t\n]/.test(text.trim())) return null;
    const matrix = text
        .split(/\r?\n/)
        .map((line) => line.split('\t').map((c) => c.trim()))
        .filter((cells) => cells.some((c) => c !== ''));
    const [first = []] = matrix;
    const isHeader = /^nom$/i.test(stripAccents(first[0] || '')) && /^prenoms?$/i.test(stripAccents(first[1] || ''));
    return isHeader ? matrix.slice(1) : matrix;
}

/** Colle la matrice à partir de la ligne `row`, colonne `col` ; crée les lignes manquantes. */
export function pasteInto(rows, row, col, matrix, makeRow) {
    const out = rows.map((r) => ({ ...r }));
    matrix.forEach((cells, i) => {
        while (out.length <= row + i) out.push(makeRow());
        cells.forEach((value, j) => {
            const field = FIELDS[col + j];
            if (field) out[row + i][field] = field === 'birthDate' ? expandShortYear(value) : value;
        });
    });
    return out;
}

/** JJ/MM/AA (export tableur en année courte) → JJ/MM/20AA : les enfants sont tous nés après 2000. */
export function expandShortYear(text) {
    return text.replace(/^(\s*\d{1,2}\/\d{1,2}\/)(\d{2})(\s*)$/, '$120$2$3');
}

export function toIsoDate(text) {
    const t = expandShortYear((text || '').trim());
    let y, m, d;
    let match = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) [, d, m, y] = match.map(Number);
    else if ((match = t.match(/^(\d{4})-(\d{2})-(\d{2})$/))) [, y, m, d] = match.map(Number);
    else return null;
    const date = new Date(Date.UTC(y, m - 1, d));
    if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function ageAt(birthIso, refIso) {
    const [by, bm, bd] = birthIso.split('-').map(Number);
    const [ry, rm, rd] = refIso.split('-').map(Number);
    return ry - by - (rm < bm || (rm === bm && rd < bd) ? 1 : 0);
}

const normalize = (s) => stripAccents(s).toLowerCase().replace(/[-'’\s]+/g, ' ').trim();

export function validateRows(rows, unitStartIso) {
    const errors = {};
    const seen = new Map();
    const duplicates = [];
    for (const r of rows) {
        const e = {};
        if (!r.lastName.trim()) e.lastName = 'Nom obligatoire';
        if (!r.firstName.trim()) e.firstName = 'Prénom obligatoire';
        const iso = toIsoDate(r.birthDate);
        if (!iso) e.birthDate = 'Date au format JJ/MM/AAAA';
        else {
            const age = ageAt(iso, unitStartIso);
            if (age < 3 || age > 18) e.birthDate = `Âge au début du séjour : ${age} ans`;
        }
        const email = (r.parentEmail || '').trim();
        if (email && !isValidEmail(email)) e.parentEmail = 'Email invalide';
        if (Object.keys(e).length) errors[r.id] = e;
        else {
            const k = `${normalize(r.lastName)}|${normalize(r.firstName)}|${iso}`;
            if (seen.has(k)) duplicates.push(r.id); else seen.set(k, r.id);
        }
    }
    return { errors, duplicates, valid: Object.keys(errors).length === 0 };
}

export function toPayload(rows) {
    return {
        version: 1,
        children: rows.map((r) => {
            const email = (r.parentEmail || '').trim().toLowerCase();
            return {
                id: r.id, lastName: r.lastName.trim(), firstName: r.firstName.trim(), birthDate: toIsoDate(r.birthDate),
                ...(email ? { parentEmail: email } : {}),
            };
        }),
    };
}
